import {
  GetServerSideProps,
  NextApiRequest,
  NextApiResponse,
  NextPage,
} from 'next'
import omit from 'lodash.omit'
import cx from 'classnames'
import { Controller, useForm } from 'react-hook-form'
import { formatPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'
import PhoneInputWithCountry from 'react-phone-number-input/react-hook-form'
import Link from 'next/link'
import { useRouter } from 'next/router'
import css from 'styled-jsx/css'

import FormBox from '../components/FormBox'
import Button from '../components/Button'
import Container from '../components/Container'
import Input from '../components/Input'
import Layout from '../components/Layout'
import Title from '../components/Title'
import SingleSelect from '../components/contact/SingleSelect'
import { EMAIL } from '../utils/regexp'
import { textStandardValidations } from '../utils/validations'
import { getLoginSession } from '../utils/auth/auth'
import { sectorFocusOptions } from '../constants'
import { getServerSideProps as getSSRProps } from '../utils/settings'
import { BasePageProps } from '../types'

import 'react-phone-number-input/style.css'

type SignupFormInputs = {
  firstName: string
  lastName: string
  email: string
  company: string
  jobTitle: string
  phone: string
  sectorFocus?: string
  terms: boolean
}

type FormError = {
  form?: string
}

const Signup: NextPage<BasePageProps> = ({ settings }) => {
  const { query } = useRouter()

  const {
    register,
    control,
    clearErrors,
    setError,
    handleSubmit,
    formState: { errors, isValid, isSubmitSuccessful },
  } = useForm<SignupFormInputs & FormError>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  // @ts-ignore
  async function onSubmit(...args) {
    clearErrors('form')
    return handleSubmit(async (data) => {
      try {
        clearErrors('form')

        const parsedPhoneNumber = parsePhoneNumber(data.phone)
        const formattedData = omit(
          {
            ...data,
            ...(query.redirect && {
              article: query.redirect,
            }),
            phone: `+${
              parsedPhoneNumber?.countryCallingCode
            } ${formatPhoneNumber(parsedPhoneNumber?.number as string)}`,
          },
          ['terms']
        )

        const res = await fetch('/api/signup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formattedData),
        }).then((res) => res.json())
        if (!res.done) {
          throw new Error(res.message || res.data || 'Something went wrong')
        }
      } catch (error: any) {
        setError('form', {
          type: 'manual',
          message: error?.message,
        })
      }
    })(...args)
  }

  return (
    <Layout>
      <Container
        className={cx('flex flex-col', isSubmitSuccessful ? 'py-48' : 'py-32')}
      >
        {isSubmitSuccessful ? (
          <div>
            <Title
              title="Thank you for your submission"
              className={cx('success-title', components.className)}
            />
            <p className="mt-4 text-center font-inter text-xl text-black-800">
              Our team will be in touch with more details shortly.
            </p>
          </div>
        ) : (
          <>
            <Title
              title="Sign up for access to PeakLoad"
              className="text-center"
            />
            <form onSubmit={onSubmit}>
              <FormBox>
                <Input
                  placeholder="*First Name"
                  error={errors.firstName?.message}
                  containerClass="w-full mb-6"
                  {...register('firstName', textStandardValidations)}
                />
                <Input
                  placeholder="*Last Name"
                  error={errors.lastName?.message}
                  containerClass="w-full mb-6"
                  {...register('lastName', textStandardValidations)}
                />
                <Input
                  placeholder="*Email Address"
                  error={errors.email?.message}
                  type="email"
                  containerClass="w-full mb-6"
                  {...register('email', {
                    required: 'This field is required',
                    pattern: {
                      value: EMAIL,
                      message: 'Incorrect format',
                    },
                  })}
                />
                <Input
                  placeholder="*Job title"
                  error={errors.jobTitle?.message}
                  containerClass="w-full mb-6"
                  {...register('jobTitle', textStandardValidations)}
                />
                <PhoneInputWithCountry
                  name="phone"
                  placeholder="*Phone number"
                  control={control}
                  rules={{ required: true }}
                  defaultCountry="US"
                  className={cx('phone-input', components.className)}
                  withCountryCallingCode
                />
                <Input
                  placeholder="*Company"
                  error={errors.company?.message}
                  containerClass="w-full mb-6"
                  {...register('company', textStandardValidations)}
                />

                <Controller
                  render={({ field: { onChange, value, ref, ...rest } }) => (
                    <>
                      {/* @ts-ignore */}
                      <SingleSelect
                        {...rest}
                        innerRef={ref}
                        value={
                          !!value
                            ? {
                                label: value as string,
                                value: value as string,
                              }
                            : null
                        }
                        onChange={(option) => onChange(option.value)}
                        options={sectorFocusOptions}
                        placeholder="Sector Focus"
                      />
                    </>
                  )}
                  control={control}
                  name="sectorFocus"
                />

                {errors['form']?.message && (
                  <p className="mb-6 text-base font-semibold text-red-400">
                    {errors['form'].message}
                  </p>
                )}
                <label className="mb-6 flex">
                  <input
                    type="checkbox"
                    className="mr-2 mt-1"
                    {...register('terms', {
                      required: 'This field is required',
                    })}
                  />
                  <div className="font-inter leading-snug">
                    I agree to PeakLoad's{' '}
                    <a
                      href="https://www.peakload.com/terms-and-conditions"
                      target="_blank"
                      className="text-blue-500 hover:underline"
                    >
                      terms of use
                    </a>
                    , including agreeing to receive exclusive news alerts via
                    email.
                  </div>
                </label>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-8">
                  <Button disabled={!isValid}>Get started now</Button>

                  <div className="font-inter">
                    Already a member?{' '}
                    <Link href="/login" passHref>
                      <a className="cursor-pointer font-bold underline hover:no-underline">
                        Login
                      </a>
                    </Link>
                  </div>
                </div>
              </FormBox>
            </form>
          </>
        )}
      </Container>
      {components.styles}
    </Layout>
  )
}

const components = css.resolve`
  .success-title {
    @apply text-center;
    letter-spacing: -0.03em;
  }
  .phone-input {
    @apply mb-6 w-full rounded-[4px] border border-solid border-black bg-white py-4 px-6 font-inter;
  }
  .phone-input :global(input) {
    @apply pl-2 outline-none;
  }
`

export const getServerSideProps: GetServerSideProps = async function ({
  req,
  res,
}) {
  const session = await getLoginSession(
    req as NextApiRequest,
    res as NextApiResponse
  )
  if (session) {
    return {
      redirect: {
        permanent: false,
        destination: '/',
      },
      props: {},
    }
  }

  const pageProps = await getSSRProps(
    req as NextApiRequest,
    res as NextApiResponse
  )
  return pageProps
}

export default Signup
